<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { AdvancedAnalysisSchema } from '../models/AdvancedAnalysisSchema'

  const props = defineProps<{
    visible: boolean
    advancedAnalysisSchemas: Array<AdvancedAnalysisSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'advanced-analysis-selected': [cfg: AdvancedAnalysisSchema]
  }>()

  const availableAnalyses = ref<AdvancedAnalysisSchema[]>([])
  const filteredAnalyses = ref<AdvancedAnalysisSchema[]>([])
  const showHiddenAnalyses = ref<boolean>(false)
  const loadingAnalyses = ref<boolean>(false)
  const hasErrorLoadingAnalyses = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group analyses by category
  const groupedAnalyses = ref<Record<string, AdvancedAnalysisSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredAnalyses.value = availableAnalyses.value.filter((schema: AdvancedAnalysisSchema) => {
        return (
          (schema.description && schema.description.toLowerCase().includes(search)) ||
          (schema.display_name && schema.display_name.toLowerCase().includes(search))
        )
      })
    } else {
      filteredAnalyses.value = [...availableAnalyses.value]
    }
    groupAnalysesByCategory()
  }

  const groupAnalysesByCategory = () => {
    const grouped: Record<string, AdvancedAnalysisSchema[]> = {}
    filteredAnalyses.value.forEach((cfg) => {
      if (!grouped[cfg.category]) {
        grouped[cfg.category] = []
      }
      grouped[cfg.category].push(cfg)
    })
    groupedAnalyses.value = grouped
  }

  const selectAnalysis = (cfg: AdvancedAnalysisSchema) => {
    emit('advanced-analysis-selected', cfg)
    // Keep dialog open to allow adding multiple items easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableAnalyses()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableAnalyses()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableAnalyses = () => {
    availableAnalyses.value = props.advancedAnalysisSchemas
    filteredAnalyses.value = props.advancedAnalysisSchemas
    groupAnalysesByCategory()
  }

  // Watch for visibility changes to load analyses if needed
  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.advancedAnalysisSchemas,
    (newItems) => {
      availableAnalyses.value = [...newItems]
      filteredAnalyses.value = [...newItems]
      groupAnalysesByCategory()
    }
  )

  defineExpose({
    availableAnalyses
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Advanced Analysis"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="advanced-analysis-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search advanced analyses by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox class="ml-2 flex-shrink-0 whitespace-nowrap" v-model:checked="showHiddenAnalyses">
          Show All Advanced Analyses
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingAnalyses" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available advanced analyses...</p>
      </div>

      <!-- Analyses List -->
      <div v-else id="configsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingAnalyses"
          v-for="(analyses, category) in groupedAnalyses"
          :key="category"
          class="mb-6"
        >
          <template
            v-if="analyses.filter((x) => (showHiddenAnalyses ? x : x.is_visible === true)).length > 0"
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ analyses.length }} {{ analyses.length === 1 ? 'analysis' : 'analyses' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="cfg in analyses.filter((x) =>
                  showHiddenAnalyses ? x : x.is_visible === true
                )"
                :key="cfg.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectAnalysis(cfg)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ cfg.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">{{ cfg.description }}</p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ cfg.fields.length }} field{{ cfg.fields.length !== 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingAnalyses"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading advanced analyses.
        </div>
        <!-- No Results -->
        <div
          v-if="!hasErrorLoadingAnalyses && filteredAnalyses.length === 0"
          class="py-8 text-center"
        >
          <p>
            {
              {
                searchText.trim()
                  ? 'No advanced analyses found matching your search.'
                  : 'No advanced analyses available.'
              }
            }
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs text-gray-400">
            Try adjusting your search terms or clear the search to browse all analyses.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any advanced analysis above to add it.</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .advanced-analysis-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #configsListContainer {
    flex: 1;
  }
  #configsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #configsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #configsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #configsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
